import React, { useEffect, useState } from 'react'
import { GlobalTable, HeaderLine, Td, Th, Tr } from '../../../../components/styles/table-style'
import { useOutletContext } from 'react-router-dom'
import { FashionOutletType } from '..'
import axios from '../../../../config/axios'
import FashionApiService from '../../../../services/FashionApiService'
import { LoadMoreProvider, useLoadMore } from '../../../../contexts/LoadMoreContext'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import ActionsDropdown from './ActionsDropdown'

type CompanyType = {
  id: number;
  companyName: string;
}

type OrderType = {
  id: number
  companyId: number
  createdAt: string
  startDateOfDelivery: string
  quantityItems: string
  quantityPieces: string
  status: string
  totalCost: number
  saleStatus: string
}

const FashionOrdersTableContent: React.FC = () => {
  const { setTabIndex } = useOutletContext<FashionOutletType>()
  const [orders, setOrders] = useState<OrderType[]>([])
  const [companies, setCompanies] = useState<CompanyType[]>([])
  const [currentCount, setCurrentCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const { hasNextPage, setHasNextPage, isFetchingNextPage, setIsFetchingNextPage, setFetchNextPageFunction } = useLoadMore()

  const columns = ["Nº do pedido", "Cliente", "Data pedido", "Data entrega", "Qtd. de itens", "Qtd. de peças", "Status", "Total (R$)", "Venda", ""]

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, {
          params: { 'filters[0]': 1 }
        })
        setCompanies(Array.isArray(response.data) ? response.data : [])
      } catch (error: any) {
        console.error('Erro ao buscar as empresas:', error.response?.data || error.message || error)
      }
    }

    fetchCompanies()
  }, [])

  const getCompanyName = (companyId: number) => {
    const company = companies.find(c => c.id === companyId)
    return company ? company.companyName : 'N/A'
  }

  const fetchData = async (page: number, append: boolean = false) => {
    try {
      if (append) setIsFetchingNextPage(true)
      
      const size = 10
      const [ordersResponse, countResponse] = await Promise.all([
        FashionApiService.getFashionOrders(page, size),
        page === 0 ? FashionApiService.getFashionOrdersCount() : Promise.resolve(null)
      ])

      if (append) {
        setOrders(prev => [...prev, ...ordersResponse.data])
        setCurrentCount((prev: number) => prev + ordersResponse.data.length)
      } else {
        setOrders(ordersResponse.data)
        setCurrentCount(ordersResponse.data.length)
      }

      if (countResponse) {
        setTotalCount(countResponse.data.total)
      }

      const newTotal = countResponse ? countResponse.data.total : totalCount
      const newCurrent = append ? currentCount + ordersResponse.data.length : ordersResponse.data.length
      setHasNextPage(newCurrent < newTotal)
    } catch (error) {
      console.error('Erro ao buscar os pedidos:', error)
    } finally {
      if (append) setIsFetchingNextPage(false)
    }
  }

  useEffect(() => {
    fetchData(0)
  }, [])

  useEffect(() => {
    setFetchNextPageFunction(() => fetchData(Math.floor(currentCount / 10), true))
  }, [currentCount])

  const hasMore = currentCount < totalCount

  return (
    <GlobalTable>
      <thead>
        <HeaderLine>
          {columns.map((item, index) => (
            <Th key={index}>{item}</Th>
          ))}
        </HeaderLine>
      </thead>
      <tbody>
        {orders.map((order) => (
          <Tr key={order.id}>
            <Td>{order.id}</Td>
            <Td>{getCompanyName(order.companyId)}</Td>
            <Td>{new Date(order.createdAt).toLocaleDateString('pt-BR')}</Td>
            <Td>
                {
                  new Date(order.startDateOfDelivery).toISOString().substring(0, 10).split('-').reverse().join('/')
                }
            </Td>
            <Td>{order.quantityItems}</Td>
            <Td>{order.quantityPieces}</Td>
            <Td>{order.status}</Td>
            <Td>R${order.totalCost?.toFixed(2) ?? '0,00'}</Td>
            <Td>{order.saleStatus}</Td>
            <Td>
              <ActionsDropdown 
                order={order} 
                onDelete={() => setOrders((prev: OrderType[]) => prev.filter((o) => o.id !== order.id))}
                onUpdate={() => fetchData(0)}
              />
            </Td>
          </Tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={columns.length}>
            <LoadMoreButton
              currentCount={currentCount}
              totalCount={totalCount}
              hasMore={hasMore}
              onLoadMore={() => fetchData(Math.floor(currentCount / 10), true)}
              isLoading={isFetchingNextPage}
            />
          </td>
        </tr>
      </tfoot>
    </GlobalTable>
  )
}

const FashionOrdersTable: React.FC = () => {
  return (
    <LoadMoreProvider>
      <FashionOrdersTableContent />
    </LoadMoreProvider>
  )
}

export default FashionOrdersTable
